import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  Visibility as VisibilityIcon,
  People as PeopleIcon,
  Timer as TimerIcon,
  ExitToApp as ExitToAppIcon,
  TouchApp as TouchAppIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  DataUsage as DataUsageIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FF6B6B', '#6B66FF'];

const AnalyticsDashboard = () => {
  const { accountId, microSiteId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // 30 days ago
  const [endDate, setEndDate] = useState(new Date());
  const [period, setPeriod] = useState('day');
  const [tabValue, setTabValue] = useState(0);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageAnalytics, setPageAnalytics] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingTestData, setGeneratingTestData] = useState(false);

  // Fetch microsite pages
  useEffect(() => {
    const fetchPages = async () => {
      try {
        const response = await fetch(`/api/accounts/${accountId}/microsites/${microSiteId}/pages`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch pages: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        setPages(data);
      } catch (error) {
        console.error('Error fetching pages:', error);
        setError(error.message);
      }
    };

    fetchPages();
  }, [accountId, microSiteId]);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);

      try {
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        
        // Add a timestamp to prevent caching
        const timestamp = new Date().getTime();

        const response = await fetch(
          `/api/analytics/accounts/${accountId}/microsites/${microSiteId}/analytics?startDate=${formattedStartDate}&endDate=${formattedEndDate}&period=${period}&_t=${timestamp}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch analytics: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Analytics data received:', data);
        setAnalyticsData(data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [accountId, microSiteId, startDate, endDate, period]);

  // Fetch page-specific analytics when a page is selected
  useEffect(() => {
    if (!selectedPage) {
      setPageAnalytics(null);
      return;
    }

    const fetchPageAnalytics = async () => {
      try {
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        
        // Add a timestamp to prevent caching
        const timestamp = new Date().getTime();

        const response = await fetch(
          `/api/analytics/accounts/${accountId}/microsites/${microSiteId}/pages/${selectedPage}/analytics?startDate=${formattedStartDate}&endDate=${formattedEndDate}&_t=${timestamp}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch page analytics: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Page analytics data received:', data);
        setPageAnalytics(data);
      } catch (error) {
        console.error('Error fetching page analytics:', error);
        setError(error.message);
      }
    };

    fetchPageAnalytics();
  }, [accountId, microSiteId, selectedPage, startDate, endDate]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handlePageChange = (event) => {
    setSelectedPage(event.target.value);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    
    // Re-fetch analytics data
    const fetchData = async () => {
      try {
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        
        // Add a timestamp to prevent caching
        const timestamp = new Date().getTime();

        const response = await fetch(
          `/api/analytics/accounts/${accountId}/microsites/${microSiteId}/analytics?startDate=${formattedStartDate}&endDate=${formattedEndDate}&period=${period}&_t=${timestamp}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch analytics: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Analytics data refreshed:', data);
        setAnalyticsData(data);
        
        // If a page is selected, also refresh page analytics
        if (selectedPage) {
          const pageResponse = await fetch(
            `/api/analytics/accounts/${accountId}/microsites/${microSiteId}/pages/${selectedPage}/analytics?startDate=${formattedStartDate}&endDate=${formattedEndDate}&_t=${timestamp}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
              }
            }
          );
          
          if (pageResponse.ok) {
            const pageData = await pageResponse.json();
            setPageAnalytics(pageData);
          }
        }
      } catch (error) {
        console.error('Error refreshing analytics:', error);
        setError('Failed to refresh analytics data. Please try again.');
      } finally {
        setRefreshing(false);
      }
    };
    
    fetchData();
  };

  // Add function to generate test data
  const handleGenerateTestData = async () => {
    if (!window.confirm('This will generate sample analytics data for testing purposes. Continue?')) {
      return;
    }
    
    setGeneratingTestData(true);
    
    try {
      const response = await fetch(
        `/api/analytics/accounts/${accountId}/microsites/${microSiteId}/generate-test-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          body: JSON.stringify({
            pageId: selectedPage || null,
            days: 30,
            viewsPerDay: 20,
            interactionsPerDay: 40
          })
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to generate test data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Test data generation result:', result);
      
      alert(`Test data generated successfully!\n\nPage Views: ${result.results.pageViews}\nInteractions: ${result.results.interactions}\nSummaries: ${result.results.summaries}`);
      
      // Refresh the data
      handleRefresh();
    } catch (error) {
      console.error('Error generating test data:', error);
      alert(`Failed to generate test data: ${error.message}`);
    } finally {
      setGeneratingTestData(false);
    }
  };

  if (loading && !analyticsData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !analyticsData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1">
          There was an error loading the analytics data. Please try again later.
        </Typography>
      </Container>
    );
  }

  // Format data for charts
  const formatTimeSeriesData = (data) => {
    if (!data || !data.timeSeriesData) return [];
    return data.timeSeriesData.map(item => ({
      ...item,
      period: formatPeriodLabel(item.period, period)
    }));
  };

  const formatPeriodLabel = (periodValue, periodType) => {
    if (periodType === 'day') {
      // Format as MM/DD
      const date = new Date(periodValue);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    } else if (periodType === 'week') {
      // Format as Week of MM/DD
      const date = new Date(periodValue);
      return `Week of ${date.getMonth() + 1}/${date.getDate()}`;
    } else if (periodType === 'month') {
      // Format as MMM YYYY
      const [year, month] = periodValue.split('-');
      const date = new Date(year, month - 1);
      return date.toLocaleString('default', { month: 'short', year: 'numeric' });
    }
    return periodValue;
  };

  const formatPageViewsByPage = (data) => {
    if (!data || !data.pageViewsByPage) return [];
    
    return Object.entries(data.pageViewsByPage).map(([pageId, count]) => {
      const page = pages.find(p => p.id === pageId);
      return {
        name: page ? page.title : 'Unknown Page',
        value: count
      };
    }).sort((a, b) => b.value - a.value);
  };

  const formatInteractionsByType = (data) => {
    if (!data || !data.interactionsByType) return [];
    
    return Object.entries(data.interactionsByType).map(([type, count]) => ({
      name: formatInteractionType(type),
      value: count
    })).sort((a, b) => b.value - a.value);
  };

  const formatInteractionType = (type) => {
    switch (type) {
      case 'click':
        return 'Clicks';
      case 'scroll_depth':
        return 'Scroll Depth';
      case 'form_submit':
        return 'Form Submissions';
      case 'page_exit':
        return 'Page Exits';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Analytics Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<DataUsageIcon />}
            onClick={handleGenerateTestData}
            disabled={generatingTestData}
          >
            {generatingTestData ? 'Generating...' : 'Generate Test Data'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/accounts/${accountId}/sites/${microSiteId}`)}
          >
            Back to Site Overview
          </Button>
        </Box>
      </Box>
      
      <Paper sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={period}
                label="Time Period"
                onChange={handlePeriodChange}
              >
                <MenuItem value="day">Daily</MenuItem>
                <MenuItem value="week">Weekly</MenuItem>
                <MenuItem value="month">Monthly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Page</InputLabel>
              <Select
                value={selectedPage || ''}
                label="Page"
                onChange={handlePageChange}
                displayEmpty
              >
                <MenuItem value="">All Pages</MenuItem>
                {pages.map(page => (
                  <MenuItem key={page.id} value={page.id}>{page.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Page Views" />
        <Tab label="Interactions" />
        {selectedPage && <Tab label="Page Details" />}
      </Tabs>
      
      {/* Overview Tab */}
      {tabValue === 0 && analyticsData && (
        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <VisibilityIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Page Views
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mt: 2 }}>
                  {analyticsData.overview.totalPageViews.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Unique Visitors
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mt: 2 }}>
                  {analyticsData.overview.uniqueVisitors.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ExitToAppIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Bounce Rate
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mt: 2 }}>
                  {analyticsData.overview.bounceRate.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TimerIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Avg. Duration
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mt: 2 }}>
                  {formatDuration(analyticsData.overview.avgDuration)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Time Series Chart */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Traffic Over Time" />
              <Divider />
              <CardContent>
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={formatTimeSeriesData(analyticsData)}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="pageViews" name="Page Views" stroke="#8884d8" activeDot={{ r: 8 }} />
                      <Line type="monotone" dataKey="interactions" name="Interactions" stroke="#82ca9d" />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Page Views by Page */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Page Views by Page" />
              <Divider />
              <CardContent>
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={formatPageViewsByPage(analyticsData)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {formatPageViewsByPage(analyticsData).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Interactions by Type */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Interactions by Type" />
              <Divider />
              <CardContent>
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={formatInteractionsByType(analyticsData)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {formatInteractionsByType(analyticsData).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Page Views Tab */}
      {tabValue === 1 && analyticsData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Page Views Over Time" />
              <Divider />
              <CardContent>
                <Box height={400}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={formatTimeSeriesData(analyticsData)}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="pageViews" name="Page Views" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Top Pages" />
              <Divider />
              <CardContent>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={tableHeaderStyle}>Page</th>
                        <th style={tableHeaderStyle}>Views</th>
                        <th style={tableHeaderStyle}>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formatPageViewsByPage(analyticsData).map((page, index) => (
                        <tr key={index}>
                          <td style={tableCellStyle}>{page.name}</td>
                          <td style={tableCellStyle}>{page.value.toLocaleString()}</td>
                          <td style={tableCellStyle}>
                            {((page.value / analyticsData.overview.totalPageViews) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Interactions Tab */}
      {tabValue === 2 && analyticsData && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Interactions Over Time" />
              <Divider />
              <CardContent>
                <Box height={400}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={formatTimeSeriesData(analyticsData)}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="interactions" name="Interactions" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Interaction Types" />
              <Divider />
              <CardContent>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={tableHeaderStyle}>Interaction Type</th>
                        <th style={tableHeaderStyle}>Count</th>
                        <th style={tableHeaderStyle}>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formatInteractionsByType(analyticsData).map((interaction, index) => (
                        <tr key={index}>
                          <td style={tableCellStyle}>{interaction.name}</td>
                          <td style={tableCellStyle}>{interaction.value.toLocaleString()}</td>
                          <td style={tableCellStyle}>
                            {((interaction.value / analyticsData.overview.totalInteractions) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Page Details Tab */}
      {tabValue === 3 && selectedPage && pageAnalytics && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <VisibilityIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Page Views
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mt: 2 }}>
                  {pageAnalytics.overview.totalPageViews.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Unique Visitors
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mt: 2 }}>
                  {pageAnalytics.overview.uniqueVisitors.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ExitToAppIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Bounce Rate
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mt: 2 }}>
                  {pageAnalytics.overview.bounceRate.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TimerIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Avg. Duration
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mt: 2 }}>
                  {formatDuration(pageAnalytics.overview.avgDuration)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Page Views Over Time" />
              <Divider />
              <CardContent>
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={pageAnalytics.pageViewsTimeSeries}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="views" name="Page Views" stroke="#8884d8" activeDot={{ r: 8 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Interactions by Type" />
              <Divider />
              <CardContent>
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={formatInteractionsByType(pageAnalytics)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {formatInteractionsByType(pageAnalytics).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Top Referrers" />
              <Divider />
              <CardContent>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={tableHeaderStyle}>Referrer</th>
                        <th style={tableHeaderStyle}>Count</th>
                        <th style={tableHeaderStyle}>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(pageAnalytics.referrers || {}).map(([domain, count], index) => (
                        <tr key={index}>
                          <td style={tableCellStyle}>{domain}</td>
                          <td style={tableCellStyle}>{count.toLocaleString()}</td>
                          <td style={tableCellStyle}>
                            {((count / pageAnalytics.overview.totalPageViews) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

// Helper function to format duration in seconds to minutes and seconds
const formatDuration = (seconds) => {
  if (!seconds) return '0s';
  
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  
  return `${minutes}m ${remainingSeconds}s`;
};

// Custom label for pie charts
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// Table styles
const tableHeaderStyle = {
  textAlign: 'left',
  padding: '12px 8px',
  borderBottom: '1px solid #e0e0e0',
  backgroundColor: '#f5f5f5'
};

const tableCellStyle = {
  padding: '12px 8px',
  borderBottom: '1px solid #e0e0e0'
};

export default AnalyticsDashboard; 